import { useUIStore, type Toast } from '@/stores/useUIStore';

/**
 * Pure toast list — presentational, SSR-testable with plain props.
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-03-candidate-badges
 */
export function ToastList({
  toasts,
  onRemove,
}: {
  toasts: Toast[];
  onRemove: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      data-testid="ui-toast-host"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 'min(92vw, 420px)',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          data-testid="ui-toast"
          role="status"
          onClick={() => onRemove(t.id)}
          style={{
            pointerEvents: 'auto',
            cursor: 'pointer',
            padding: '10px 14px',
            borderRadius: 10,
            fontSize: '0.85rem',
            fontWeight: 600,
            textAlign: 'center',
            color: '#0b1220',
            background:
              t.type === 'error' ? '#f87171' :
              t.type === 'warning' ? '#fbbf24' :
              t.type === 'info' ? '#38bdf8' :
              '#4ade80',
            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
          }}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

/**
 * Global toast host — renders the zustand `toasts` queue once at app root.
 * Required host for M09 tier-crossing celebrations (`useUIStore.addToast`).
 *
 * Note: zustand v5 serves `getInitialState()` to react-dom/server, so the
 * host glue itself is not SSR-asserted — tests cover `ToastList` + store.
 *
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-03-candidate-badges
 */
export function UIToastHost() {
  const toasts = useUIStore((s) => s.toasts);
  const removeToast = useUIStore((s) => s.removeToast);
  return <ToastList toasts={toasts} onRemove={removeToast} />;
}
